import React, { useMemo, useRef, useState } from 'react';
import { FormExportMenu } from './FormExport/FormExportMenu';
import useFormSubmission from 'shared/hooks/useFormSubmission';
import CallDeflectionFormAttachment from './CallDeflectionFormAttachment';
import styled from 'styled-components/macro';
import { ChannelViewClinicPetParentFragment } from 'shared/types/graphql';
import { useMutation } from '@apollo/client';
import { UPDATE_ONE_FORM_SUBMISSION } from 'pages/Conversations/graphql/mutations';
import { Box, HStack, useOutsideClick, Flex } from '@televet/kibble-ui/build/chakra';
import PetAvatar from 'shared/components/Avatars/PetAvatar';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Link } from '@televet/kibble-ui/build/components/Link';
import { Icon } from '@televet/kibble-ui/build/components/Icon';

interface IFormAttachment {
  entityId?: string | null;
  clinicPetParentId: string;
  clinicPetParents?: (ChannelViewClinicPetParentFragment | undefined | null)[];
}

const FormAttachment = ({ entityId, clinicPetParentId, clinicPetParents }: IFormAttachment): JSX.Element => {
  const [isChangingAuthorizedUser, setIsChangingAuthorizedUser] = useState(false);
  const {
    data: formSubmission,
    loading: formSubmissionLoading,
    openFormSubmissionEditor,
    reload: retryLoadingFormSubmission,
  } = useFormSubmission({ formSubmissionId: entityId, subscribeWhenUnsubmitted: true });

  const dropDownRef = useRef(null);

  useOutsideClick({
    ref: dropDownRef,
    handler: () => setIsChangingAuthorizedUser(false),
  });

  const [updateFormSubmission] = useMutation(UPDATE_ONE_FORM_SUBMISSION);

  const handleChangeAuthorizedUser = (clinicPetParentId: string | undefined): void => {
    //change clinicPetParentId in formSubmission
    if (clinicPetParentId) {
      updateFormSubmission({
        variables: { where: { id: entityId }, data: { clinicPetParent: { connect: { id: clinicPetParentId } } } },
      });
      setIsChangingAuthorizedUser(false);
    }
  };

  // Local variables
  const formName = formSubmissionLoading ? 'Loading' : formSubmission?.formTemplate?.title || 'Unknown';
  const isFormSubmitted = !!formSubmission?.submittedAt;
  const formContent = formSubmission?.completedContent || formSubmission?.draftContent;
  const parsedFormContent = typeof formContent === 'string' ? JSON.parse(formContent) : formContent;
  const petInfo = useMemo(() => formSubmission?.clinicPet, [formSubmission]);

  // Form Export Menu
  const formProp = {
    id: entityId || '',
    title: formSubmission?.formTemplate?.title || 'Form',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    content: parsedFormContent as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pet: formSubmission?.clinicPet as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any;

  const petParent = formSubmission?.clinicPetParent;
  const petParentProp = {
    id: clinicPetParentId,
    name: petParent ? `${petParent.firstName} ${petParent.lastName}` : '',
  };

  const assignedId = formSubmission?.clinicPet?.id;
  const petsProp =
    formSubmission?.clinicPetParent?.pets?.map((p) => ({
      id: p.id,
      name: p.name,
      pimsId: p.pimsId,
      species: p.speciesType,
      assigned: p.id === assignedId,
    })) || [];

  const isCallDeflection = formSubmission?.isCallDeflectionForm;
  const loadingOrSuccess = Object.keys(parsedFormContent || {}).length || formSubmissionLoading;
  const failed = !loadingOrSuccess;

  const authorizedClinicPetParentCandidates = useMemo(() => {
    return (
      clinicPetParents?.filter((clinicPetParent) => !clinicPetParent?.isDeleted && clinicPetParent?.isActive) || []
    );
  }, [clinicPetParents]);

  if (loadingOrSuccess && isCallDeflection) {
    return (
      <CallDeflectionFormAttachment
        formSubmission={formSubmission}
        formSubmissionLoading={formSubmissionLoading}
        clinicPetParentId={clinicPetParentId}
      />
    );
  }

  return (
    <Box className="FormAttachment__Container" data-testid="form-attachment" w="100%">
      {loadingOrSuccess && (
        <HStack
          justify="flex-start"
          spacing={3}
          onClick={(e): void => {
            e.stopPropagation();
            openFormSubmissionEditor(clinicPetParentId);
          }}
        >
          <Icon name="form" />
          <Text size="sm" as="span" isTruncated noOfLines={1} cursor="pointer">
            {formName}
          </Text>
          {!!petInfo && (
            <PetAvatar
              name={petInfo.name}
              speciesType={petInfo.speciesType}
              isActive={petInfo.computedIsActive}
              isDeceased={petInfo.computedIsDeceased}
              photoUrl={petInfo.photoUrl}
              size="xs"
              ml={4}
            />
          )}
          <Flex
            data-testid="form-completed-indicator"
            bg={isFormSubmitted ? 'background.success' : 'background.alpha.pressed'}
            w={4}
            h={4}
            borderRadius="full"
          >
            {isFormSubmitted && <Icon size="sm" name="checkmarkCircle" variant="success" />}
          </Flex>
          {isFormSubmitted && parsedFormContent && (
            <FormExportMenu form={formProp} pets={petsProp} petParent={petParentProp} />
          )}
        </HStack>
      )}
      {failed && (
        <HStack justify="flex-start" spacing={3} flex="1 1 auto">
          <Icon name="form" />
          <Text>Failed to load form</Text>
          <Link
            iconProps={{ name: 'doubleArrowCircle', position: 'start' }}
            variant="danger"
            onClick={(): void => retryLoadingFormSubmission()}
          >
            Try Again
          </Link>
          {!!petInfo && (
            <PetAvatar
              name={petInfo.name}
              speciesType={petInfo.speciesType}
              isActive={petInfo.computedIsActive}
              isDeceased={petInfo.computedIsDeceased}
              photoUrl={petInfo.photoUrl}
              size="xs"
            />
          )}
          <Flex
            data-testid="form-completed-indicator"
            bg={isFormSubmitted ? 'background.success' : 'background.alpha.pressed'}
            w={4}
            h={4}
            borderRadius="full"
          >
            {isFormSubmitted && <Icon size="sm" name="checkmarkCircle" variant="success" />}
          </Flex>
        </HStack>
      )}
      {(clinicPetParents?.length || 0) > 1 && !formSubmission?.submittedAt && (
        <AuthorizedClinicPetParent>
          Authorized to submit: {formSubmission?.clinicPetParent?.firstName} {formSubmission?.clinicPetParent?.lastName}{' '}
          <ChangeAuthorizedUser onClick={(): void => setIsChangingAuthorizedUser((state) => !state)}>
            Change
          </ChangeAuthorizedUser>
          {isChangingAuthorizedUser && (
            <AuthorizedUserDropDown ref={dropDownRef}>
              {authorizedClinicPetParentCandidates.map((clinicPetParent) => {
                return (
                  <ClinicPetParentOption
                    key={`clinicPetParent-option-${clinicPetParent?.id}`}
                    onClick={(): void => {
                      handleChangeAuthorizedUser(clinicPetParent?.id);
                    }}
                  >
                    {clinicPetParent?.firstName} {clinicPetParent?.lastName}
                  </ClinicPetParentOption>
                );
              })}
            </AuthorizedUserDropDown>
          )}
        </AuthorizedClinicPetParent>
      )}
    </Box>
  );
};

const AuthorizedClinicPetParent = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  margin-left: 10px;
`;

const ChangeAuthorizedUser = styled.div`
  color: #00a0e3;
  font-size: 12px;
  cursor: pointer;
  margin: 2px 5px 0 5px;
`;

const AuthorizedUserDropDown = styled.span`
  position: absolute;
  left: 48px;
  bottom: 20px;
  margin-left: 10px;
  margin-top: 20px;
  width: 200px;
  background-color: var(--chakra-colors-background-default);
  border: 1px solid #c1c1c1;
  border-radius: 8px;
  padding: 10px;
  z-index: 999;
`;

const ClinicPetParentOption = styled.div`
  cursor: pointer;
  :hover {
    background-color: #c7eefaa4;
  }
`;

export default FormAttachment;
